import moment from 'moment-timezone';
import { partition } from 'lodash';

import { OrderInfo } from 'common/types';

export interface OrderInfoWithRentalDate extends OrderInfo {
	rentalDate: moment.Moment;
}

export enum ROW_TYPES {
	DATE_TEXT = 'date_text',
	WALKIN_TEXT = 'walkin_text',
	RESERVATIONS_TEXT = 'reservations_text',
	RENTAL_CARD = 'rental_card',
	NO_RENTALS_TODAY = 'no_rentals_today_text',
	MORE_EXPIRED_RENTALS_BUTTON = 'more_expired_rentals_button',
	SEARCH_INPUT = 'search_input',
	SEARCH_FILTERS = 'search_filters',
}

export type ReactVirtualizedRowForRentals =
	| { type: ROW_TYPES.NO_RENTALS_TODAY }
	| { type: ROW_TYPES.DATE_TEXT; date: moment.Moment }
	| { type: ROW_TYPES.WALKIN_TEXT }
	| { type: ROW_TYPES.RESERVATIONS_TEXT }
	| { type: ROW_TYPES.RENTAL_CARD; data: OrderInfoWithRentalDate }
	| { type: ROW_TYPES.MORE_EXPIRED_RENTALS_BUTTON }
	| { type: ROW_TYPES.SEARCH_INPUT }
	| { type: ROW_TYPES.SEARCH_FILTERS };

const getMoreExpiredRentalsButton = (): ReactVirtualizedRowForRentals => ({
	type: ROW_TYPES.MORE_EXPIRED_RENTALS_BUTTON,
});

const getNoRentalsComponent = (): ReactVirtualizedRowForRentals => ({
	type: ROW_TYPES.NO_RENTALS_TODAY,
});

const getDateTextRow = (date: moment.Moment): ReactVirtualizedRowForRentals => ({
	type: ROW_TYPES.DATE_TEXT,
	date,
});

const getWalkInTextRow = (): ReactVirtualizedRowForRentals => ({
	type: ROW_TYPES.WALKIN_TEXT,
});

const getReservationsTextRow = (): ReactVirtualizedRowForRentals => ({
	type: ROW_TYPES.RESERVATIONS_TEXT,
});

const getRentalCardRow = (rental: OrderInfoWithRentalDate): ReactVirtualizedRowForRentals => ({
	type: ROW_TYPES.RENTAL_CARD,
	data: rental,
});

const isSameDay = (a: moment.Moment, b: moment.Moment) => a.isSame(b, 'day');

const sortByDate = (a: OrderInfoWithRentalDate, b: OrderInfoWithRentalDate) =>
	a.rentalDate.diff(b.rentalDate);

const isToday = (dateStringAsMoment: moment.Moment) => dateStringAsMoment.isSame(moment(), 'day');

const rentalsWithMomentisedRentalDates = (rentals: OrderInfo[]): OrderInfoWithRentalDate[] =>
	rentals.map((rental) => {
		const rentalDate = rental.startDate
			? moment(rental.startDate)
			: rental.created
			? moment(rental.created)
			: moment();
		return { ...rental, rentalDate };
	});

const rentalsWithMomentisedReturnDates = (rentals: OrderInfo[]): OrderInfoWithRentalDate[] =>
	rentals.map((rental) => {
		const rentalDate = rental.returnTimeNext
			? moment(rental.returnTimeNext)
			: rental.created
			? moment(rental.created)
			: moment();
		return { ...rental, rentalDate };
	});

const sortedRentalsByWalkinsAndReservations = (rentalsWithDates: OrderInfoWithRentalDate[]) => {
	const [todayRentals, laterRentals] = partition(rentalsWithDates, ({ rentalDate }) =>
		isToday(rentalDate),
	);
	const [todayWalkins, todayReservations] = partition(todayRentals, ['channel', 'STORE']);

	const sortedTodayWalkins = todayWalkins.sort(sortByDate);
	const sortedTodayReservations = todayReservations.sort(sortByDate);
	const sorterLaterRentals = laterRentals.sort(sortByDate);

	return [sortedTodayWalkins, sortedTodayReservations, sorterLaterRentals];
};

const getLaterReservationsRows = (
	currentRental: OrderInfoWithRentalDate,
	index: number,
	allNotTodayReservations: OrderInfoWithRentalDate[],
): ReactVirtualizedRowForRentals[] => {
	const previousRental = allNotTodayReservations[index - 1];
	return index === 0 || !isSameDay(currentRental.rentalDate, previousRental.rentalDate)
		? [getDateTextRow(currentRental.rentalDate), getRentalCardRow(currentRental)]
		: [getRentalCardRow(currentRental)];
};

const getNotExpiredRentalsRows = (rentalsWithRentalDates: OrderInfoWithRentalDate[]) => {
	const [
		todayWalkIns,
		todayReservations,
		notTodayReservations,
	] = sortedRentalsByWalkinsAndReservations(rentalsWithRentalDates);

	const rows: ReactVirtualizedRowForRentals[] = [];

	rows.push(getDateTextRow(moment()));

	if (todayWalkIns.length === 0 && todayReservations.length === 0) {
		rows.push(getNoRentalsComponent());
	}

	if (todayWalkIns.length > 0) {
		rows.push(getWalkInTextRow(), ...todayWalkIns.flatMap(getRentalCardRow));
	}

	if (todayReservations.length > 0) {
		rows.push(getReservationsTextRow(), ...todayReservations.flatMap(getRentalCardRow));
	}

	if (notTodayReservations.length > 0) {
		rows.push(...notTodayReservations.flatMap(getLaterReservationsRows));
	}

	return rows;
};

export const generateRowsForBookedRentalsView = (
	rentalsToRender: OrderInfo[],
	showExpired: boolean,
) => {
	const rentalsWithRentalDates = rentalsWithMomentisedRentalDates(rentalsToRender);

	return showExpired
		? [
				...rentalsWithRentalDates.flatMap(getLaterReservationsRows),
				...[getMoreExpiredRentalsButton()],
		  ]
		: getNotExpiredRentalsRows(rentalsWithRentalDates);
};

export const generateRowsForActiveRentalsView = (rentalsToRender: OrderInfo[]) => {
	const rentalsWithRentalDates = rentalsWithMomentisedReturnDates(rentalsToRender);

	return rentalsWithRentalDates.length > 0
		? rentalsWithRentalDates.flatMap(getLaterReservationsRows)
		: [];
};
